import { Component, Input, forwardRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { I18nService } from '../../services/i18n.service';

@Component({
  selector: 'app-form-rich-text-display',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="form-section rich-text-display-section">
      <label *ngIf="label" [attr.for]="id" class="rich-text-title">{{ label }}</label>
      <div
        [id]="id"
        class="rich-text-content"
        [style.font-size]="fontSize"
        [style.color]="color"
        [style.text-align]="textAlign"
        [style.line-height]="lineHeight"
        [style.max-width]="maxWidth"
        [style.background-color]="backgroundColor"
        [style.padding]="padding"
        [style.border-radius]="borderRadius"
        [innerHTML]="safeHtmlContent">
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormRichTextDisplayComponent),
      multi: true
    }
  ],
  styleUrls: ['./form-rich-text-display.component.scss']
})
export class FormRichTextDisplayComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label: string = ''; // 標籤
  @Input() id: string = ''; // id 用於對應 label
  @Input() name: string = ''; // name 用於表單提交
  @Input() content: string = ''; // 富文本內容 (支援HTML)
  @Input() fontSize: string = '14px'; // 字型大小
  @Input() color: string = '#333333'; // 文字顏色
  @Input() textAlign: 'left' | 'center' | 'right' | 'justify' = 'left'; // 文字對齊方式
  @Input() lineHeight: string = '1.5'; // 行間距
  @Input() maxWidth: string = '100%'; // 最大寬度
  @Input() backgroundColor?: string; // 背景顏色
  @Input() padding?: string; // 內邊距
  @Input() borderRadius?: string; // 圓角設定

  safeHtmlContent: SafeHtml = ''; // 安全的 HTML 內容
  value: string = ''; // 綁定的值
  private isUpdating: boolean = false; // 防止循環更新的標誌
  private _externalValueSet = false; // 標記是否已由外部設定值

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private sanitizer: DomSanitizer,
    private i18nService: I18nService
  ) {}

  ngOnInit(): void {
    // 初始化時更新內容
    this.updateSafeHtmlContent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 檢查是否有非 content 屬性的變更（如樣式屬性）
    const hasStyleChanges = Object.keys(changes).some(key =>
      ['fontSize', 'color', 'textAlign', 'lineHeight', 'maxWidth', 'backgroundColor', 'padding', 'borderRadius', 'label'].includes(key)
    );

    // 檢查是否有 content 屬性變更
    const hasContentChanges = changes['content'];

    // 樣式屬性變更總是要響應
    if (hasStyleChanges) {
      // 樣式變更不需要更新 HTML 內容，只需要讓模板重新渲染
      return;
    }

    // 只有在外部未設定值的情況下才響應 content 變更
    if (hasContentChanges && !this._externalValueSet) {
      this.updateSafeHtmlContent();
    }
  }

  // 更新安全的 HTML 內容
  private updateSafeHtmlContent(): void {
    // 防止在更新過程中觸發循環更新
    if (this.isUpdating) {
      return;
    }

    const contentToUse = this.content || this.value || '';

    if (contentToUse && contentToUse.trim()) {
      // 使用 DomSanitizer 來安全地處理 HTML 內容
      try {
        // 基本的 HTML 清理 - 移除危險的標籤和屬性
        const cleanHtml = this.sanitizeHtml(contentToUse);
        this.safeHtmlContent = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
      } catch (error) {
        console.error('Error processing rich text content:', error);
        this.safeHtmlContent = this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(contentToUse));
      }
    } else {
      // 如果沒有內容，設置預設佔位符
      const placeholderText = this.i18nService.translate('form.builder.rich_text_placeholder');
      this.safeHtmlContent = this.sanitizer.bypassSecurityTrustHtml(`<p class="placeholder-text">${placeholderText}</p>`);
    }
  }

  // 基本的 HTML 清理函數
  private sanitizeHtml(html: string): string {
    // 允許的標籤
    const allowedTags = [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'blockquote', 'pre', 'code',
      'div', 'span'
    ];

    // 允許的屬性
    const allowedAttributes: { [key: string]: string[] } = {
      'a': ['href', 'title', 'target'],
      'img': ['src', 'alt', 'title', 'width', 'height'],
      '*': ['style', 'class']
    };

    // 移除 script, style, iframe 等危險標籤
    let cleanHtml = html.replace(/<(script|style|iframe|object|embed|form)[^>]*>[\s\S]*?<\/\1>/gi, '');

    // 移除 javascript: 協議
    cleanHtml = cleanHtml.replace(/javascript:/gi, '');

    // 移除 on* 事件處理器
    cleanHtml = cleanHtml.replace(/\son\w+\s*=\s*[^>]*/gi, '');

    return cleanHtml;
  }

  // HTML 轉義函數
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    // 防止循環更新
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    this._externalValueSet = true; // 標記為外部設定

    try {
      if (value !== undefined && value !== this.value) {
        this.value = value;
        this.content = value || '';
        this.updateSafeHtmlContent();
      }
    } finally {
      // 使用 setTimeout 確保在下一個變更檢測週期後重置標誌
      setTimeout(() => {
        this.isUpdating = false;
      }, 0);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 富文本顯示元件本身是唯讀的，不需要處理 disabled 狀態
  }

  // 手動更新內容的方法，供外部呼叫
  updateContent(newContent: string): void {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    this._externalValueSet = true; // 標記為外部設定

    try {
      this.content = newContent;
      this.value = newContent;
      this.updateSafeHtmlContent();

      // 通知外部值已改變
      this.onChange(newContent);
      this.onTouched();
    } finally {
      setTimeout(() => {
        this.isUpdating = false;
      }, 0);
    }
  }
}
